// src/components/Survey/SurveyBuilderSection.js
import React, { useCallback } from "react";
import { DragDropContext } from "react-beautiful-dnd";
import SurveyBuilder, { questionTypes } from "./SurveyBuilder";

function SurveyBuilderSection({
  steps,
  setSteps,
  currentStep,
  setCurrentStep,
  handleQuestionChange,
  handleQuestionSettingsChange,
  handleGlobalSettingsChange,
  handleQuestionStylesChange,
  handleAddChoice,
  handleChoiceChange,
  handleDeleteQuestion,
  handleDuplicateQuestion,
  settings,
  files,
  setFiles,
  processedFileName,
  setProcessedFileName,
  enqueueSnackbar,
  getRootProps,
  getInputProps,
  isDeleting,
  setIsDeleting,
  projectError,
}) {
  const buildQuestionFromType = useCallback((draggedQuestionType) => {
    if (draggedQuestionType.type === "PAGE_BREAK") {
      return {
        id: `${draggedQuestionType.type}-${Date.now()}`,
        type: "PAGE_BREAK",
        choices: []
      };
    }

    let choices = [];
    if (draggedQuestionType.type === "SINGLE_CHOICE" || draggedQuestionType.type === "MULTI_CHOICE") {
      choices = [
        { text: "Choice 1", value: "choice_1", index: 0, tags: [] },
        { text: "Choice 2", value: "choice_2", index: 1, tags: [] },
      ];
    } else if (draggedQuestionType.type === "LIKERT_SCALE") {
      choices = [
        { text: "Strongly Disagree", value: "strongly_disagree", index: 0, tags: [] },
        { text: "Disagree", value: "disagree", index: 1, tags: [] },
        { text: "Neutral", value: "neutral", index: 2, tags: [] },
        { text: "Agree", value: "agree", index: 3, tags: [] },
        { text: "Strongly Agree", value: "strongly_agree", index: 4, tags: [] },
      ];
    }

    return {
      id: `${draggedQuestionType.type}-${Date.now()}`,
      type: draggedQuestionType.type,
      question: `<p>Enter your ${draggedQuestionType.label.toLowerCase()} question here</p>`,
      description: "",
      settings: {
        styles: {
          question: { fontWeight: "normal", fontStyle: "normal", fontSize: "14px", lineHeight: "1.15", letterSpacing: "0px" },
          description: { fontWeight: "normal", fontStyle: "normal", fontSize: "14px", lineHeight: "1.15", letterSpacing: "0px" },
        },
      },
      tags: [],
      choices,
      isMandatory: false,
    };
  }, []);

  const onDragEnd = useCallback(
    (result) => {
      const { source, destination } = result;

      if (!destination) return;

      if (isDeleting) {
        console.log("Drag-and-drop ignored: Deletion in progress");
        return;
      }

      if (source.droppableId === "question-types") {
        const draggedQuestionType = questionTypes.find((qt) => qt.id === result.draggableId);
        if (!draggedQuestionType) return;
        const newQuestion = buildQuestionFromType(draggedQuestionType);

        if (destination.droppableId === "survey-builder") {
          setSteps((prev) => [...prev, newQuestion]);
          setCurrentStep(steps.length);
          return;
        }

        if (destination.droppableId === "steps-sidebar") {
          setSteps((prev) => {
            const newSteps = [...prev];
            newSteps.splice(destination.index, 0, newQuestion);
            return newSteps;
          });
          setCurrentStep(destination.index);
          return;
        }
      }

      if (source.droppableId === "steps-sidebar" && destination.droppableId === "steps-sidebar") {
        setSteps((prev) => {
          const newSteps = [...prev];
          const [reorderedStep] = newSteps.splice(source.index, 1);
          newSteps.splice(destination.index, 0, reorderedStep);
          return newSteps;
        });
        setCurrentStep((prevCurrentStep) => {
          if (prevCurrentStep === source.index) return destination.index;
          if (source.index < destination.index && prevCurrentStep > source.index && prevCurrentStep <= destination.index) {
            return prevCurrentStep - 1;
          }
          if (source.index > destination.index && prevCurrentStep >= destination.index && prevCurrentStep < source.index) {
            return prevCurrentStep + 1;
          }
          return prevCurrentStep;
        });
        return;
      }

      if (source.droppableId === "steps-sidebar" && destination.droppableId === "survey-builder") {
        // Main survey area acts as a selector for existing steps when dragged from the sidebar.
        setCurrentStep(source.index);
      }
    },
    [isDeleting, setSteps, setCurrentStep, steps.length, buildQuestionFromType]
  );

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <SurveyBuilder
        steps={steps}
        setSteps={setSteps}
        currentStep={currentStep}
        setCurrentStep={setCurrentStep}
        handleQuestionChange={handleQuestionChange}
        handleQuestionSettingsChange={handleQuestionSettingsChange}
        handleGlobalSettingsChange={handleGlobalSettingsChange}
        handleQuestionStylesChange={handleQuestionStylesChange}
        handleAddChoice={handleAddChoice}
        handleChoiceChange={handleChoiceChange}
        handleDeleteQuestion={handleDeleteQuestion}
        handleDuplicateQuestion={handleDuplicateQuestion}
        settings={settings}
        questionTypes={questionTypes}
        acceptedFiles={files}
        setAcceptedFiles={setFiles}
        getRootProps={getRootProps}
        getInputProps={getInputProps}
        enqueueSnackbar={enqueueSnackbar}
        isDeleting={isDeleting}
        processedFileName={processedFileName}
        setProcessedFileName={setProcessedFileName}
        projectError={projectError}
      />
    </DragDropContext>
  );
}

export default SurveyBuilderSection;
